import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  ComponentDetail,
  ComponentList,
  type Component,
  type CreateAdjustmentBody,
  type CreateComponentBody,
  type InstallBody,
  type MoveBody,
  type RemoveBody,
  type UpdateComponentBody,
} from '@klunkers/shared'
import { api } from '@/api/http'
import { cachedRead } from '@/lib/cache'

/**
 * Parts (components). A part has its own identity and history: fitting,
 * removing and moving never copy it, they only open and close install windows.
 */
export const useComponentsStore = defineStore('components', () => {
  const byBike = ref<Record<string, Component[]>>({})
  const spares = ref<Component[]>([])
  const details = ref<Record<string, ComponentDetail>>({})
  /** Bikes whose build sheet is being shown from the device cache. */
  const offlineBikes = ref<Record<string, boolean>>({})

  async function loadForBike(bikeId: string) {
    const { value, offline } = await cachedRead(
      `parts:${bikeId}`,
      async () =>
        (await api(`/components?bike_id=${bikeId}`, { schema: ComponentList })).components,
    )
    byBike.value = { ...byBike.value, [bikeId]: value }
    offlineBikes.value = { ...offlineBikes.value, [bikeId]: offline }
    return value
  }

  async function loadSpares() {
    spares.value = (await api('/components?spare=true', { schema: ComponentList })).components
    return spares.value
  }

  async function load(id: string) {
    const detail = await api(`/components/${id}`, { schema: ComponentDetail })
    details.value = { ...details.value, [id]: detail }
    return detail
  }

  /** After any change, drop list caches that may now be stale and keep the fresh detail. */
  function remember(detail: ComponentDetail) {
    details.value = { ...details.value, [detail.id]: detail }
    byBike.value = {}
    spares.value = spares.value.filter((s) => s.id !== detail.id)
    return detail
  }

  const create = async (body: CreateComponentBody) =>
    remember(await api('/components', { method: 'POST', body, schema: ComponentDetail }))
  const update = async (id: string, body: UpdateComponentBody) =>
    remember(await api(`/components/${id}`, { method: 'PATCH', body, schema: ComponentDetail }))
  const install = async (id: string, body: InstallBody) =>
    remember(
      await api(`/components/${id}/install`, { method: 'POST', body, schema: ComponentDetail }),
    )
  const removeFromBike = async (id: string, body: RemoveBody) =>
    remember(
      await api(`/components/${id}/remove`, { method: 'POST', body, schema: ComponentDetail }),
    )
  const move = async (id: string, body: MoveBody) =>
    remember(await api(`/components/${id}/move`, { method: 'POST', body, schema: ComponentDetail }))

  const addAdjustment = async (id: string, body: CreateAdjustmentBody) =>
    remember(
      await api(`/components/${id}/adjustments`, { method: 'POST', body, schema: ComponentDetail }),
    )
  const removeAdjustment = async (id: string, adjustmentId: string) =>
    remember(
      await api(`/components/${id}/adjustments/${adjustmentId}`, {
        method: 'DELETE',
        schema: ComponentDetail,
      }),
    )

  async function destroy(id: string) {
    await api(`/components/${id}`, { method: 'DELETE' })
    const { [id]: _gone, ...rest } = details.value
    details.value = rest
    byBike.value = {}
    spares.value = spares.value.filter((s) => s.id !== id)
  }

  return {
    byBike,
    spares,
    details,
    offlineBikes,
    loadForBike,
    loadSpares,
    load,
    create,
    update,
    install,
    removeFromBike,
    move,
    addAdjustment,
    removeAdjustment,
    destroy,
  }
})
